import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {Filters, FiltersConfig} from '../../../shared/models/filter';
import {Option} from '../../../shared/models/option';
import {UrlService} from '../../../core/services/url.service';
import {take} from 'rxjs/operators';
import {SessionService} from '../../../core/services/session.service';
import {ClickService} from '../../../core/services/click.service';
import {Subscription} from 'rxjs';
import {ToggleInputWrapperComponent} from '../toggle-input-wrapper/toggle-input-wrapper.component';
import {FormsModule} from '@angular/forms';
import {PopoverSelectDevicesComponent} from '../popover-multi-select/object-types/devices/popover-select-devices.component';
import {PopoverSelectHubsComponent} from '../popover-multi-select/object-types/hubs/popover-select-hubs.component';
import {SearchService} from '../../../core/services/search.service';

@Component({
  selector: 'motn-filters-menu',
  templateUrl: './filters-menu.component.html',
  styleUrls: ['./filters-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ToggleInputWrapperComponent,
    FormsModule,
    PopoverSelectDevicesComponent,
    PopoverSelectHubsComponent,
  ],
})
export class FiltersMenuComponent implements OnInit, OnDestroy {

  @Input() filtersConfig: FiltersConfig;
  filters: Filters;

  deviceMenuOpen: boolean = false;
  deviceFilterToggled: boolean = false;

  hubMenuOpen: boolean = false;
  hubFilterToggled: boolean = false;

  ownerFilterToggled: boolean = false;

  metadataSearchToggled: boolean = false;

  /**
   * This is for the filters menu itself
   */
  overlayPositionsOuter: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  }];

  /**
   * This is for menus that are spawned within the filters menu
   */
  overlayPositionsInner: ConnectedPosition[] = [{
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
  }];

  @Output() clickedOutsideMenu$: EventEmitter<null> = new EventEmitter<null>();

  private clickSub: Subscription;


  constructor(
    private eRef: ElementRef,
    private clickService: ClickService,
    private urlService: UrlService,
    private session: SessionService,
    private searchService: SearchService,
  ) {
  }


  ngOnInit(): void {
    this.urlService.filters$.pipe(
      take(1),
    ).subscribe({
      next: (filters) => {
        if (!filters) {
          filters = {};
        }

        this.filters = filters;
        this.prepareFilters();
      },
    });

    this.clickSub = this.clickService.click$.subscribe(
      ($el) => {
        if (!this.eRef.nativeElement.contains($el)) {
          this.clickedOutsideMenu$.emit(null);
        }
      },
    );
  }


  ngOnDestroy() {
    this.clickSub?.unsubscribe();
  }


  private prepareFilters() {
    const filters = this.filters;

    if (!filters.devices) {
      filters.devices = [];
    }

    if (!filters.hubs) {
      filters.hubs = [];
    }

    if (!filters.owners) {
      filters.owners = [];
    }

    if (filters.devices.length) {
      this.deviceFilterToggled = true;
    }

    if (filters.hubs.length) {
      this.hubFilterToggled = true;
    }

    if (filters.owners.length) {
      this.ownerFilterToggled = true;
    }

    // Always sync metadata state with SearchService
    this.metadataSearchToggled = !!filters.metadata;
    this.searchService.setMetadataSearchEnabled(!!filters.metadata);
  }


  openDeviceMenu() {
    this.deviceMenuOpen = true;
  }


  closeDeviceMenu() {
    this.deviceMenuOpen = false;
  }


  toggleDeviceMenu() {
    if (this.deviceMenuOpen) {
      this.closeDeviceMenu();
    } else {
      this.openDeviceMenu();
    }
  }


  onClickDeviceToggle() {
    setTimeout(() => {
      if (this.deviceFilterToggled) {
        if (!this.filters.devices.length) {
          this.openDeviceMenu();
        }
      }

      this.sendFiltersToUrl();
    });
  }


  onClickDeviceOption(option: Option) {
    const deviceId = option.id;
    const devices = this.filters.devices;

    if (option.selected) {
      if (!devices.includes(deviceId)) {
        devices.push(deviceId);
      }

      this.deviceFilterToggled = true;
    } else {
      const idx = devices.indexOf(deviceId);

      if (idx > -1) {
        devices.splice(idx, 1);
      }

      if (!devices.length) {
        this.deviceFilterToggled = false;
      }
    }

    this.sendFiltersToUrl();
  }


  openHubMenu() {
    this.hubMenuOpen = true;
  }


  closeHubMenu() {
    this.hubMenuOpen = false;
  }


  toggleHubMenu() {
    if (this.hubMenuOpen) {
      this.closeHubMenu();
    } else {
      this.openHubMenu();
    }
  }


  onClickHubToggle() {
    setTimeout(() => {
      if (this.hubFilterToggled) {
        if (!this.filters.hubs.length) {
          this.openHubMenu();
        }
      }

      this.sendFiltersToUrl();
    });
  }


  onClickHubOption(option: Option) {
    const hubId = option.id;
    const hubs = this.filters.hubs;

    if (option.selected) {
      if (!hubs.includes(hubId)) {
        hubs.push(hubId);
      }

      this.hubFilterToggled = true;
    } else {
      const idx = hubs.indexOf(hubId);

      if (idx > -1) {
        hubs.splice(idx, 1);
      }

      if (!hubs.length) {
        this.hubFilterToggled = false;
      }
    }

    this.sendFiltersToUrl();
  }


  onClickOwnerToggle() {
    setTimeout(() => {
      const owners = this.filters.owners;
      const userId = this.session.userId;

      if (!owners.includes(userId)) {
        owners.length = 0;
        owners.push(userId);
      }

      this.sendFiltersToUrl();
    });
  }


  onClickMetadataToggle() {
    setTimeout(() => {
      this.filters.metadata = this.metadataSearchToggled;
      // Sync with SearchService for backward compatibility
      this.searchService.setMetadataSearchEnabled(this.metadataSearchToggled);
      this.sendFiltersToUrl();
    });
  }


  private sendFiltersToUrl() {
    const emission: Filters = JSON.parse(JSON.stringify(this.filters));

    // This will retain selected devices while
    // telling outside listeners no devices are
    // selected (allowing for toggling back on
    // later without having to re-select devices)
    if (!this.deviceFilterToggled) {
      emission.devices.length = 0;
    }

    // This will retain selected hubs while
    // telling outside listeners no hubs are
    // selected (allowing for toggling back on
    // later without having to re-select hubs)
    if (!this.hubFilterToggled) {
      emission.hubs.length = 0;
    }

    // This will retain selected owners while
    // telling outside listeners no owners are
    // selected (allowing for toggling back on
    // later without having to re-select owners)
    if (!this.ownerFilterToggled) {
      emission.owners.length = 0;
    }

    this.urlService.setFilters(emission);
  }
}
